import { runInAction } from 'mobx'
import rootStore from '../..' // Import the root store
import { Documentation } from '../../../cls/modules/documentation'
import { setDocumentation } from '../../../api/modules/api-documentation'
import { cleanObject, warnPt } from '../../../config'
import { responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'
import { translate as t } from '../../../cls/TranslationContext'
const documentationStore = rootStore.documentation

/**
 * @async
 * @function newDocumentation
 * @description Creates a new documentation item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new documentation item.
 * @returns {Promise<void>}
 */
export const newDocumentation = async (newId) => {
	warnPt(newId)
	runInAction(() => {
		requireDocumentationById(newId)
	})
}

/**
 * @function requireDocumentationById
 * @description Retrieves a documentation item by its ID from the store. If it doesn't exist, creates a new documentation item.
 * @param {string} documentationId - The ID of the documentation item to retrieve.
 * @returns {Documentation} The retrieved or newly created documentation item.
 */
export const requireDocumentationById = (documentationId) => {
	warnPt(documentationId)
	let documentation = documentationStore.documentation[documentationId]
	if (documentation) {
		return documentation
	}
	runInAction(() => {
		documentationStore.documentation[documentationId] = new Documentation(
			documentationId
		)
	})
	return documentationStore.documentation[documentationId]
}

/**
 * @async
 * @function actLoadDocumentation
 * @description Loads a documentation item from the server by its ID and updates the store.
 * @param { function } navigate - The navigation function.
 * @param {string} documentationId - The ID of the documentation item to load.
 * @returns {Promise<void>}
 */
export const actLoadDocumentation = wrapWithLoading(
	async (documentationId) => {
		// TODO: remove hardcoded _response object when API is ready for this module
		// const _response = await getDocumentation(documentationId)

		const _response = {
			status: 'success',
			data: {
				documentation_id: '2753599c-6dba-40f5-983e-87d2e81bc8a9',
				name: t('__overview%_project'),
				description: t('single.document.overview.info'),
				body: `# h1 Heading
## h2 Heading
### h3 Heading
#### h4 Heading
##### h5 Heading
###### h6 Heading

2nd paragraph. *Italic*, **bold**. Itemized lists
look like:

  * this one
  * that one
  * the other one`,
				created_by: 'be5590fa-a0bd-4012-8680-f43cbf4c71bb',
				created_at: '2024-10-16T12:00:00Z',
				modified: '2024-10-16T13:00:00Z',
				to_delete: false
			}
		}

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			const { data } = _response
			runInAction(() => {
				const _storeItm = requireDocumentationById(documentationId)
				_storeItm.updateFromServer(data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Documentation' }
)

/**
 * @async
 * @function actSaveDocumentation
 * @description Saves a documentation item to the server.
 * @param {string} documentationId - The ID of the documentation item to save.
 * @param {Object} full - The full data of the documentation item to save.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveDocumentation = wrapWithLoading(
	async (documentationId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				documentationId,
				full,
				requireDocumentationById,
				rootStore.uiLstDocumentation,
				false
			)

			return true
		}

		full = cleanObject(full)
		const _response = await setDocumentation(documentationId, {
			name: full.name,
			description: full.description,
			body: full.body,
			appType: full.appType,
			created_by: full.created_by
		})

		const { data } = _response
		const hasError = data ? responseHasError(data) : true

		if (hasError) {
			throw data
		} else {
			const _storeItm = requireDocumentationById(documentationId)
			_storeItm.setModified(data.modified)
		}
		return true // No exceptions were thrown, so return true
	},
	{ loadingMessage: 'Saving Documentation' }
)
